//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from './service';
import ObservationTypeModel from '../models/observation-type.model';
import BroodBreederModel from '../models/brood-breeder.model';

/**
 * Service for managing brood breeders data (Master Data).
 *
 * Provides methods to fetch and manage brood breeders-related data.
 * Inherits CRUD operations and HTTP utilities from the generic {@link Service} class.
 *
 * @module BroodBreedersService
 */
@Injectable({
  providedIn: 'root'
})
export class BroodBreedersService extends Service<BroodBreederModel> {

  /**
   * Stores ongoing HTTP request subscriptions.
   * Used to cancel active requests and prevent memory leaks.
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * Constructs an instance of BroodBreedersService.
   *
   * @param {HttpClient} httpClient - The HttpClient service for making HTTP requests.
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/blubay/broodbreeders/');
  }

  /**
   * Fetches the complete list of brood breeders (Master Data).
   *
   * @method
   * @returns {Promise<Array<BroodBreederModel>>} A Promise resolving with an array of BroodBreederModel objects.
   * @throws {HttpErrorResponse} Throws an HTTP error if the request fails.
   *
   * Description:
   * This method is typically called in {@link BroodBreedersComponent} to retrieve the full brood breeders list.
   */
  public getAllBroodBreedersList(): Promise<Array<BroodBreederModel>> {
    return new Promise<Array<BroodBreederModel>>((resolve, reject) => {
      const request = this.httpClient
        .get<Array<BroodBreederModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=1000&order=new`, this.httpOptions)
        .subscribe(
          response => resolve(response),
          (error: HttpErrorResponse) => reject(error)
        );
      this.ongoingRequests.push(request);
    });
  }

  /**
   * Cancels all ongoing HTTP requests by unsubscribing from their observables.
   * Ensures proper cleanup and prevents memory leaks.
   *
   * Typically called in components when they are destroyed to cancel pending requests.
   *
   * @method
   * @returns {void}
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach(request => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
